import { FormattedMessage, useIntl } from 'react-intl';

import TrashIcon from '@/assets/icons/trash.svg';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormSelect } from '@/components/ui/select';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useCurrencySymbol } from '@/hooks/use-currency-symbol';
import { ZIPRECRUITER } from '@/types/channels';

import {
  getChannelConstraints,
  numericInputSetValueAs,
} from '../utils/channel-utils';
import { ZIPRECRUITER_BUDGET_PRESETS } from '../validation/constants';
import { useChannelFormContext } from './channel-form-context';

interface OpeningBudgetProps {
  /** Form-state key (channel slug or, for Vonq, product ID). */
  channelKey: string;
  openingId: string;
  showDeleteButton: boolean;
  isFree: boolean;
}

export function OpeningBudget({
  channelKey,
  openingId,
  showDeleteButton,
  isFree,
}: OpeningBudgetProps) {
  const { removeOpeningFromChannel } = useChannelFormContext();
  const { hookForm } = useJobPostingContext();
  const {
    getValues,
    formState: { errors },
  } = hookForm;

  const errorMessage =
    errors?.channels?.[channelKey]?.openingBudgets?.[openingId]?.message;

  return (
    <div
      key={openingId}
      className="flex min-h-8 flex-col justify-center gap-1 px-4"
    >
      <div className="flex items-center gap-2">
        {showDeleteButton && (
          <Button
            type="button"
            variant="icon"
            size="icon"
            onClick={() => removeOpeningFromChannel(openingId)}
            // className="size-8"
          >
            <img src={TrashIcon} alt="Delete" />
          </Button>
        )}

        <Typography variant="body1" className="min-w-0 flex-1 truncate">
          {getValues(`jobDetails.${openingId}.title`)}
        </Typography>

        <BudgetInput
          channelKey={channelKey}
          openingId={openingId}
          isFree={isFree}
        />
      </div>
      {errorMessage && (
        <Typography variant="small1" className="!text-fx-rose text-right">
          {errorMessage}
        </Typography>
      )}
    </div>
  );
}

interface BudgetInputProps {
  channelKey: string;
  openingId: string;
  isFree: boolean;
}

function BudgetInput({ channelKey, openingId, isFree }: BudgetInputProps) {
  const intl = useIntl();
  const { formatCurrency } = useCurrencySymbol();
  const { recalculateChannelBudget } = useChannelFormContext();
  const { hookForm } = useJobPostingContext();
  const {
    control,
    register,
    watch,
    formState: { errors },
  } = hookForm;

  const {
    vonqDetails,
    slug: channelSlug,
    openingBudgets,
    budgetAllocation,
  } = watch(`channels.${channelKey}`);
  const { isBudgetReadOnly } = getChannelConstraints(channelSlug, vonqDetails);
  const errorMessage =
    errors?.channels?.[channelKey]?.openingBudgets?.[openingId]?.message;

  if (isFree) {
    return (
      <Typography variant="body2" className="text-fx-neutral-600">
        <FormattedMessage
          defaultMessage="Free"
          description="Free budget label on the channel card"
        />
      </Typography>
    );
  }

  if (isBudgetReadOnly || budgetAllocation === 'evenly') {
    return (
      <Typography variant="body2" className="text-fx-neutral-600">
        {openingBudgets[openingId]
          ? formatCurrency(openingBudgets[openingId])
          : '-'}
      </Typography>
    );
  }

  if (channelSlug === ZIPRECRUITER) {
    return (
      <FormSelect
        name={`channels.${channelKey}.openingBudgets.${openingId}`}
        control={control}
        options={ZIPRECRUITER_BUDGET_PRESETS}
        isMultiSelect={false}
        className="!w-25 ml-auto shrink-0 rounded-sm"
        placeholder={intl.formatMessage({
          defaultMessage: 'Select Spend',
          description:
            'ZipRecruiter multi-opening flow: pick a spend tier for this job',
        })}
        onChange={() => recalculateChannelBudget()}
      />
    );
  }

  return (
    <Input
      isNumber
      {...register(`channels.${channelKey}.openingBudgets.${openingId}`, {
        setValueAs: numericInputSetValueAs,
        onChange: () => recalculateChannelBudget(),
      })}
      placeholderLabel="$"
      placeholder={intl.formatMessage({
        defaultMessage: 'Budget',
        description:
          'Per-opening dollar cap when posting a job to this channel',
      })}
      className="!w-25 ml-auto shrink-0"
      error={errorMessage}
      showError={false}
    />
  );
}
